import { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';

interface ServiceSectionProps {
  id: string;
  title: string;
  intro: string;
  bulletHeading: string;
  bullets: string[];
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
  contactLabel: string;
  onOpenContact: (inquiryType?: string) => void;
}

export function ServiceSection({
  id,
  title,
  intro,
  bulletHeading,
  bullets,
  imageUrl,
  imageAlt,
  reverse = false,
  contactLabel,
  onOpenContact,
}: ServiceSectionProps) {
  const containerRef = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-15%', '15%']);
  const headingId = `${id}-heading`;

  return (
    <section
      id={id}
      ref={containerRef}
      tabIndex={-1}
      aria-labelledby={headingId}
      className="relative w-full h-auto min-h-screen md:h-screen md:min-h-[600px] flex items-center overflow-hidden py-16 md:py-0 focus:outline-none"
    >
      {imageUrl ? (
        <motion.div
          aria-hidden="true"
          style={{ y: prefersReducedMotion ? 0 : y }}
          className="absolute inset-0 w-full h-[130%] -top-[15%] z-0"
        >
          <img src={imageUrl} alt={imageAlt} className="w-full h-full object-cover" />
        </motion.div>
      ) : null}

      <div aria-hidden="true" className="absolute inset-0 bg-slate-dark/70 z-10" />

      <div className="relative z-20 w-full max-w-7xl mx-auto px-6 md:px-12">
        <div className={`max-w-xl ${reverse ? 'ml-auto' : ''}`}>
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: reverse ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: prefersReducedMotion ? 0 : 1 }}
          >
            <h2 id={headingId} className="font-serif text-3xl md:text-5xl lg:text-6xl text-white mb-6">
              {title}
            </h2>
            <div aria-hidden="true" className="w-12 h-[2px] bg-bronze mb-6" />
            <div className="font-sans text-base md:text-xl text-textlight/80 leading-relaxed mb-8">
              <p className="mb-6">{intro}</p>
              <p className="block text-sm uppercase tracking-widest text-textlight/60 mb-3">
                {bulletHeading}
              </p>
              <ul className="space-y-1.5 text-base text-textlight/80">
                {bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2">
                    <span aria-hidden="true" className="mt-2 w-1 h-1 rounded-full bg-bronze shrink-0" />
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => onOpenContact(title)}
              className="inline-flex items-center gap-2 font-sans text-base text-white hover:text-bronze underline underline-offset-4 decoration-bronze decoration-2 hover:decoration-bronze/70 transition-colors min-h-[44px]"
            >
              {contactLabel}
              <span aria-hidden="true">→</span>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
